<template>
  <button
    :disabled="disabled || loading"
    @click="emit('click')"
    class="flex items-center justify-center transition-all duration-300 border"
    :class="[
      disabled
        ? 'bg-gray-200 cursor-not-allowed border-transparent'
        : [buttonClasses, 'cursor-pointer border'],
      [focusClass, sizeClass, rounded],
    ]"
    :type="type"
  >
    <BaseLoader v-if="loading" :size="loaderSizeClass" />
    <div v-else class="flex items-center space-x-1.25">
      <span>{{ label }}</span>
      <slot name="icon" />
    </div>
  </button>
</template>

<script lang="ts" setup>
import { computed } from "vue";

const emit = defineEmits(["click"]);
export type btnVariant =
  | "primary"
  | "danger"
  | "danger-secondary"
  | "outline"
  | "secondary"
  | "blue"
  | "white";

type Props = {
  label?: string;
  variant?: btnVariant;
  customClass?: string;
  type?: "button" | "submit";
  disabled?: boolean;
  loading?: boolean;
  size?: "xs" | "sm" | "md" | "lg";
  rounded?: string;
  color?: string;
};

const props = withDefaults(defineProps<Props>(), {
  label: "Button label",
  variant: "primary",
  customClass: "",
  type: "button",
  disabled: false,
  loading: false,
  size: "md",
  rounded: "rounded-lg",
  color: "#ffffff",
});

const sizeClasses = {
  xs: "px-3 py-1 text-sm",
  sm: "lg:px-5 px-3 h-10 lg:text-[15px] text-sm",
  md: "lg:px-5 px-3.5 h-12 sm:text-[15px] text-sm",
  lg: "px-8 py-4 text-xl",
};

const loaderSizes = {
  xs: "h-8 w-8",
  sm: "sm:size-6 sm:min-w-6 size-5.5 min-w-5.5",
  md: "sm:size-7 sm:min-w-7 size-6.5 min-w-6.5",
  lg: "h-8 w-8",
};

const variantClasses = {
  primary: "bg-primary text-white border-transparent",
  secondary: "bg-gray-200 text-black border-transparent",
  danger: "bg-rose-600 text-white border-transparent",
  "danger-secondary": "bg-rose-50 text-rose-600 border-transparent",
  blue: "bg-primary-blue text-white border-transparent",
  white: "bg-white text-black border-gray-200",
  outline:
    "border-primary-blue text-primary-blue outline-1 outline-primary-blue",
};

const focusRingClasses = {
  primary: "focus:ring-primary/50",
  secondary: "focus:ring-primary/30",
  danger: "focus:ring-red-500/50",
  "danger-secondary": "focus:ring-red-500/50",
  outline: "focus:ring-primary/50",
  blue: "focus:ring-gray-100",
  white: "focus:ring-primary-blue/50",
};

const baseClass = computed(() => {
  return variantClasses[props.variant] || variantClasses.primary;
});

const sizeClass = computed(() => {
  return sizeClasses[props.size];
});

const focusClass = computed(() => {
  return focusRingClasses[props.variant] || focusRingClasses.primary;
});

const buttonClasses = computed(() => {
  return `${baseClass.value} ${sizeClass.value} ${focusClass.value}`;
});
const loaderSizeClass = computed(() => {
  return loaderSizes[props.size] || loaderSizes.md;
});
</script>
